import {
    compose,
    over,
    spread,
    filter,
    get,
    map,
    overSome,
    first,
    negate,
    isEmpty,
    concat,
    uniq,
    compact,
    max,
    size,
    includes,
    intersection,
    values,
} from 'lodash/fp';
import { createSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { array, string, eq, number, record } from 'fp-ts';
import { ifElse } from '@mews/fp';
import { composeProps, createPropsExtender } from '@mews/framework';
import { GeneralEnterprise, Service } from '@mews-commander/data-access';
import { ApplicationState } from '@mews-commander/types';
import { FeatureFlag } from '@mews-commander/enums';

import { ResourceCategoryAssignments } from '@mews-commander/resources/src';
import { getFeatureFlagService } from '@mews-commander/legacy-app-instance';
import {
    createEntityToEntityMapper,
    createEntitySelector,
    createForeignKeyMatcher,
    createRelatedEntitiesSelector,
    mapId,
    notEmpty,
    sortByName,
    getData,
} from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { orderOrderableEntityArray } from '@mews-commander/legacy-entities/src/utils/orderableEntities';
import {
    getResourceCategoryIdsByResourceCategoryTypeName,
    getResourceCategoryTypesByResourceCategoryIds,
    getResourceCategoryTypesByServiceIds,
    getSortedResourceCategories,
} from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { areCategoriesFetching } from '@mews-commander/legacy-entities/src/ducks/categories';
import { areRatesFetching } from '@mews-commander/legacy-entities/src/ducks/rates';
import { getCities } from '@mews-commander/legacy-entities/src/ducks/cities';
import {
    getAllScopesWithPrivilege,
    hasPlatformScopeWithAccessAllEnterprises,
} from '@mews-commander/legacy-entities/src/ducks/scopes';
import { getOrderableProducts } from '@mews-commander/legacy-entities/src/ducks/products';
import { getEnterprises } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getAgeCategories } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import {
    getCurrentEnterpriseChainId,
    getCurrentEnterprise,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isCurrentEnterpriseMultiEnterpriseNewReservationEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    getAllNightUseServiceIds,
    getAllServiceIdsSelectableBySingleDay,
    getBookableServices,
    getServicesByIds,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getAvailabilityAdjustmentsUniqResourceCategoryIds } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import { getEnterpriseDefaultServiceId } from '@mews-commander/legacy-entities/src/selectors/enterpriseServices';
import { getResourceCategoryAssignments } from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { Entities } from '@mews-commander/legacy-entities/src/ducks';
import { getServiceId, getSelectedReservationPlaceId, getSelectedResourceCategoryTypeName } from './screens';

export const areServiceRelatedDataFetching: (state: ApplicationState) => boolean = overSome([
    areCategoriesFetching,
    areRatesFetching,
]);

export const canCurrentUserOverbookAnyServices = compose(
    negate(isEmpty),
    getAllScopesWithPrivilege('overbookServices')
);

// concrete selectors
// user
export const getCurrentUserAccountsWithManageReservationPrivilege = getAllScopesWithPrivilege('manageReservations');

// user enterprises
export const getCurrentUserAvailableEnterpriseIds = compose(
    // admin has no enterprise
    compact,
    uniq,
    map('enterpriseId'),
    getCurrentUserAccountsWithManageReservationPrivilege
);

export const getCurrentUserAvailableEnterprises = createEntityToEntityMapper(
    getEnterprises,
    getCurrentUserAvailableEnterpriseIds
);

const getAvailableEnterprises = createSelector(
    getCurrentEnterpriseChainId,
    // @ts-expect-error
    getCurrentUserAvailableEnterprises,
    getCurrentEnterprise,
    isCurrentEnterpriseMultiEnterpriseNewReservationEnabled,
    hasPlatformScopeWithAccessAllEnterprises,
    (
        currentChainId: string,
        allEnterprises: GeneralEnterprise[],
        currentEnterprise: GeneralEnterprise,
        multiEnterpriseEnabled: boolean,
        isPlatformScope: boolean
    ) => {
        if (!multiEnterpriseEnabled || isPlatformScope) {
            return [currentEnterprise];
        }

        if (getFeatureFlagService().hasFlag(FeatureFlag.multiChainNRSEnabled) && multiEnterpriseEnabled) {
            return allEnterprises;
        }

        return array.filter(({ chainId }: GeneralEnterprise) => chainId === currentChainId)(allEnterprises);
    }
);

export const getAvailableEnterpriseIds = compose(mapId, getAvailableEnterprises);

export const getMaximumAvailableEditableHistoryIntervalMilliseconds = compose(
    max,
    map('editableHistoryIntervalMilliseconds'),
    getAvailableEnterprises
);

// user cities
export const getCurrentUserAvailableCityIds = compose(uniq, map('cityId'), getAvailableEnterprises);

export const getCurrentUserAvailableCities = compose(
    sortByName,
    values,
    createEntityToEntityMapper(getCities, getCurrentUserAvailableCityIds)
);

export const shouldShowReservationPlaceSelector = compose(
    (enterpriseIds) => size(enterpriseIds) > 1,
    getAvailableEnterpriseIds
);

export const isReservationPlaceCity = createSelector(
    getSelectedReservationPlaceId,
    getCurrentUserAvailableCityIds,
    includes
);

// selected city
const selectedCityForeignKey = createForeignKeyMatcher('cityId', getSelectedReservationPlaceId);
export const getSelectedCityEnterprises = createRelatedEntitiesSelector(
    getAvailableEnterprises,
    selectedCityForeignKey
);
export const getSelectedEntityEnterprises = createSelector(
    isReservationPlaceCity,
    getSelectedReservationPlaceId,
    getSelectedCityEnterprises,
    getEnterprises,
    (isCity, selectedEntityId, cityEnterprises, enterprises) =>
        isCity ? cityEnterprises : [enterprises[selectedEntityId]]
);
export const getSelectedEntityEnterpriseIds = createSelector(getSelectedEntityEnterprises, mapId);

const getServicesWithSpaces = (
    enterprises: Array<{ id: string }>,
    services: $TSFixMe,
    resourceCategoryAssignments: Entities<ResourceCategoryAssignments>
) => {
    const assignments = pipe(
        resourceCategoryAssignments,
        record.collect(string.Ord)((_, resourceCategoryAssignment) => resourceCategoryAssignment)
    );
    return pipe(
        services,
        values,
        orderOrderableEntityArray,
        array.filter(
            (service: Service) =>
                enterprises.some((enterprise) => service.enterpriseId === enterprise.id) &&
                // get services that contain at least one resource (space/room)
                assignments.some((assignment) => assignment.serviceId === service.id)
        )
    );
};

const getSelectedCityServices = createSelector(
    getSelectedCityEnterprises,
    getBookableServices,
    getResourceCategoryAssignments,
    getServicesWithSpaces
);

export const getSelectedCityServiceIds = createSelector(getSelectedCityServices, mapId);

export const getSelectedEnterpriseServiceId = composeProps(
    getEnterpriseDefaultServiceId,
    createPropsExtender('enterpriseId', getSelectedReservationPlaceId)
);

export const getSelectedEnterpriseServices = createSelector(
    getSelectedReservationPlaceId,
    getBookableServices,
    getResourceCategoryAssignments,
    (selectedReservationPlaceId, services, resourceCategoryAssignments) =>
        getServicesWithSpaces([{ id: selectedReservationPlaceId }], services, resourceCategoryAssignments)
);

export const getSelectedEnterpriseServiceIds = createSelector(getSelectedEnterpriseServices, mapId);

export const getSelectedEntityServiceIds = ifElse(
    isReservationPlaceCity,
    getSelectedCityServiceIds,
    getSelectedEnterpriseServiceIds
);

const getSelectedEntityServices = composeProps(
    getServicesByIds,
    createPropsExtender('ids', getSelectedEntityServiceIds)
);

export const hasServicesSelectableBySingleDay = createSelector(
    getSelectedEntityServiceIds,
    getAllServiceIdsSelectableBySingleDay,
    compose(notEmpty, intersection)
);

export const getSelectedEntityServiceTimeUnits = createSelector(getSelectedEntityServices, (services) =>
    pipe(
        services,
        array.map((service) => getData(service).timeUnit),
        array.uniq(string.Eq)
    )
);

const hasOnlyNightlyServices = (allServices: string[], nightlyServices: string[]) => {
    const filterSelectedEntityServiceIds = allServices.filter((id) => !nightlyServices.includes(id));
    return filterSelectedEntityServiceIds.length === 0;
};

export const getHasOnlyNightlyServices = createSelector(
    getSelectedEnterpriseServiceIds,
    getAllNightUseServiceIds,
    hasOnlyNightlyServices
);

export const getHasOnlyNightlyCityServices = createSelector(
    getSelectedCityServiceIds,
    getAllNightUseServiceIds,
    hasOnlyNightlyServices
);

export const getServicesResourceCategoryTypesByResourceCategoryIds = createSelector(
    getResourceCategoryTypesByServiceIds,
    getResourceCategoryTypesByResourceCategoryIds,
    (serviceResourceCategoryTypes, availableResourceCategoryTypes) =>
        pipe(
            serviceResourceCategoryTypes,
            array.intersection(
                eq.struct({
                    name: string.Eq,
                    value: number.Eq,
                })
            )(availableResourceCategoryTypes)
        )
);

export const getResourceCategoryIdsOfSelectedResourceCategoryType = composeProps(
    getResourceCategoryIdsByResourceCategoryTypeName,
    createPropsExtender('resourceCategoryTypeName', getSelectedResourceCategoryTypeName)
);

const selectedServiceForeignKey = createForeignKeyMatcher('serviceId', getServiceId);

export const getSelectedServiceCategories = composeProps(
    getSortedResourceCategories,
    createPropsExtender('serviceId', getServiceId)
);

export const getSelectedServiceAgeCategories = createRelatedEntitiesSelector(
    getAgeCategories,
    selectedServiceForeignKey
);

const selectedResourceCategoryTypeForeignKey = createForeignKeyMatcher(
    'resourceCategoryType',
    getSelectedResourceCategoryTypeName
);
export const getSelectedServiceCategoriesByResourceCategoryType = createRelatedEntitiesSelector(
    getSelectedServiceCategories,
    selectedResourceCategoryTypeForeignKey
);

// selected service
export const getSelectedService = createEntitySelector(getBookableServices, getServiceId);

const getSelectedServiceProducts = createRelatedEntitiesSelector(getOrderableProducts, selectedServiceForeignKey);

export const getSelectedServiceProductsOfferedToEmployee: (
    state: ApplicationState,
    params: { scope: string }
) => $TSFixMe = createSelector(getSelectedServiceProducts, filter('options.offerToEmployee'));

export const getSelectedServiceDefaultProductIds = compose(
    mapId,
    filter('options.selectedByDefault'),
    getSelectedServiceProductsOfferedToEmployee
);

export const getSelectedServiceEnterpriseId = compose(get('enterpriseId'), getSelectedService);
const selectedEnterpriseForeignKey = createForeignKeyMatcher('enterpriseId', getSelectedServiceEnterpriseId);

export const isCrossEnterpriseReservation = createSelector(
    getCurrentEnterpriseId,
    getServiceId,
    getBookableServices,
    (profileEnterpriseId, serviceId, services) => {
        const service = get(serviceId, services);
        return profileEnterpriseId !== get('enterpriseId', service);
    }
);

export const filterAccountsPrivileges = (accounts: $TSFixMe, privilegeName: $TSFixMe) =>
    filter(get(['privileges', privilegeName]))(accounts);
export const getSelectedServiceAccounts = createRelatedEntitiesSelector(
    getCurrentUserAccountsWithManageReservationPrivilege,
    selectedEnterpriseForeignKey
);
export const getSelectedServiceAccountsWithOverbookPrivilege = createSelector(getSelectedServiceAccounts, (accounts) =>
    filterAccountsPrivileges(accounts, 'overbookServices')
);

export const canOverbookSelectedService = compose(negate(isEmpty), getSelectedServiceAccountsWithOverbookPrivilege);

const getSelectedServiceUserAccount = compose(
    first,
    spread(concat),
    over([getSelectedServiceAccountsWithOverbookPrivilege, getSelectedServiceAccounts])
);
export const getSelectedServiceUserProfileId = compose(get('profileId'), getSelectedServiceUserAccount);

export const getAvailabilityAdjustmentsUniqResourceCategoryIdsByResourceCategoryType = createSelector(
    getAvailabilityAdjustmentsUniqResourceCategoryIds,
    getResourceCategoryIdsOfSelectedResourceCategoryType,
    (adjustmentsCategoryIds, categoryIdsByResourceCategoryType) =>
        array.intersection(string.Eq)(categoryIdsByResourceCategoryType, adjustmentsCategoryIds)
);
